import { Component, OnInit, OnDestroy, signal, computed, inject, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { take, debounceTime } from 'rxjs/operators';

// Import available services
import { OrganisationService } from 'src/app/services/organisation.service';
import { UtilService } from 'src/app/services/util.service';
import { AuthService } from '../auth/auth.service';
import { AssessmentChemicalService } from "src/app/services/assessment-chemical.service";
import { AssessmentService } from "src/app/services/assessment.service";
import { CatalogueService } from 'src/app/services/catalogue.service';
import { CoordinatorService } from "src/app/services/coordinator.service";
import { SubstitutionService } from "src/app/services/substitution.service";
import { ProductService } from "src/app/services/product.service";
import { UserHelperService } from '../shared/user-helper.service';

@Component({
  selector: 'app-top-header',
  standalone: true,
  imports: [CommonModule, TranslateModule, NgbDropdownModule],
  templateUrl: './top-header.component.html',
  styleUrl: './top-header.component.css'
})
export class TopHeaderComponent implements OnInit, OnDestroy {
  // Services
  private router = inject(Router);
  private utilService = inject(UtilService);
  private organisationService = inject(OrganisationService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private assessmentService = inject(AssessmentService);
  private assessmentChemicalService = inject(AssessmentChemicalService);
  private catalogueService = inject(CatalogueService);
  private coordinatorService = inject(CoordinatorService);
  private substitutionService = inject(SubstitutionService);
  private productService = inject(ProductService);
  private userHelper = inject(UserHelperService);

  // Signals for reactive state
  currentUser = signal<any>(null);
  selectedOrganisation = signal<any>(null);
  selectedDepartment = signal<any>(null);
  organisation = signal<any>(null);
  isSettingsDropdownOpen = signal(false);

  // Count signals
  cataloguesCount = signal(0);
  productsCount = signal(0);
  coordinatorCount = signal(0);
  substitutionCount = signal(0);
  lrbCount = signal(0);
  arbCount = signal(0);

  // Subscription flags
  subscribesToCatalogue = signal(false);
  subscribesToProducts = signal(false);
  subscribesToExposures = signal(false);
  subscribesToReports = signal(false);
  subscribesToProjects = signal(false);
  subscribesToLawLawpoint = signal(false);
  subscribesToSamtec = signal(false);
  subscribesToLawAptor = signal(false);
  subscribesToEducation = signal(false);
  subscribesToCoordinator = signal(false);
  subscribesToSubstitution = signal(false);
  subscribesToRiskAssessment = signal(false);

  // Computed values
  isGuest = computed(() => this.currentUser()?.is_guest || false);
  isPrinter = computed(() => this.currentUser()?.is_printer || false);
  isKoncernMode = computed(() => this.userHelper.isGroupAdminInKoncernMode(this.currentUser()));
  isAdmin = computed(() =>
    this.currentUser()?.is_admin === 1 ||
    this.currentUser()?.is_local_admin === 1 ||
    this.currentUser()?.is_super_user === 1 ||
    this.currentUser()?.is_useradmin === 1 ||
    (this.userHelper.isGroupAdmin(this.currentUser()) && this.userHelper.isGroupAdminCompanyMode())
  );
  isLocalUser = computed(() => this.currentUser()?.is_local_user || false);
  isSuperUser = computed(() => this.currentUser()?.is_super_user || false);
  isMaintenanceUser = computed(() => this.currentUser()?.is_maintenance_user || false);
  isReadonlyUser = computed(() => this.currentUser()?.is_readonly_user || false);

  // Legacy properties for compatibility
  isHome = false;
  productsLang = {
    "en": 0,
    "sv": 0,
    "fi": 0
  };
  currentDepartment: any = null;
  products: any[] = [];

  // Subscriptions
  private departmentChangedSubscription: Subscription | null = null;
  private productListChangedSubscription: Subscription | null = null;
  private catalogueListChangedSubscription: Subscription | null = null;
  private coordinatorListChangedSubscription: Subscription | null = null;
  private substitutionListChangedSubscription: Subscription | null = null;
  private lrbListChangedSubscription: Subscription | null = null;
  private arbListChangedSubscription: Subscription | null = null;

  constructor(private host: ElementRef<HTMLElement>) {}

  ngOnInit() {
    var data = localStorage.getItem('currentUser', );
    if (data == null || data == undefined)
    {
        this.logout();
    }
    else
    {
        this.currentUser.set(JSON.parse(data));
    }

    this.subscribesToCatalogue.set(this.utilService.subscribesTo('subscription_catalogue'));
    this.subscribesToProducts.set(this.utilService.subscribesTo('subscription_products'));
    this.subscribesToCoordinator.set(this.utilService.subscribesTo('subscription_coordinator'));
    this.subscribesToSubstitution.set(this.utilService.subscribesTo('subscription_substitution'));
    this.subscribesToRiskAssessment.set(this.utilService.subscribesTo('subscription_risk_assessment'));
    this.subscribesToExposures.set(this.utilService.subscribesTo('subscription_exposures'));
    this.subscribesToReports.set(this.utilService.subscribesTo('subscription_reports'));
    this.subscribesToProjects.set(this.utilService.subscribesTo('subscription_projects'));
    this.subscribesToLawLawpoint.set(this.utilService.subscribesTo('subscription_lawpoint'));
    this.subscribesToLawAptor.set(this.utilService.subscribesTo('subscription_law_aptor'));
    this.subscribesToSamtec.set(this.utilService.subscribesTo('subscription_samtec'));
    this.subscribesToEducation.set(this.utilService.subscribesTo('subscription_education'));

    this.organisation.set(JSON.parse(localStorage.getItem('rootOrganisation', )));
    this.selectedDepartment.set(JSON.parse(localStorage.getItem('selectedDepartment', )));
    if (this.selectedDepartment() == null)
    {
      this.selectedDepartment.set(this.organisation());
    }
    this.selectedOrganisation.set(JSON.parse(localStorage.getItem('selectedOrganisation', )));

    this.cataloguesCount.set(0);
    this.productsCount.set(0);
    this.coordinatorCount.set(0);
    this.substitutionCount.set(0);
    this.lrbCount.set(0);
    this.arbCount.set(0);
    this.productsLang = {
      "en":0,
      "sv":0,
      "fi":0
      // more languages can be added here
    };

    this.loadProducts(this.selectedDepartment());
    this.loadCatalogue();
    this.getAllCoordinatorSubjects();
    this.getAllSubstitutionSubjects();
    this.getAllAssessmentSubjects();
    this.getAllActivityAssessments();

    // Set Login Status
    this.departmentChangedSubscription = this.organisationService.departmentChanged.subscribe((msg: any) => {
      this.organisation.set(JSON.parse(localStorage.getItem('rootOrganisation', )));
      this.selectedOrganisation.set(JSON.parse(localStorage.getItem('rootOrganisation', )));
      this.selectedDepartment.set(JSON.parse(localStorage.getItem('selectedDepartment', )));
      this.currentDepartment = this.selectedDepartment();
      this.departmentChangedSubscription.unsubscribe();
      this.ngOnInit();
    })

    if (!this.productListChangedSubscription) {
      this.productListChangedSubscription = this.productService.productListChanged
        .pipe(debounceTime(400))
        .subscribe(() => this.refreshProductCount());
    }
    if (!this.catalogueListChangedSubscription) {
      this.catalogueListChangedSubscription = this.catalogueService.catalogueListChanged
        .pipe(debounceTime(400))
        .subscribe(() => this.loadCatalogue());
    }
    if (!this.coordinatorListChangedSubscription) {
      this.coordinatorListChangedSubscription = this.coordinatorService.coordinatorListChanged
        .pipe(debounceTime(400))
        .subscribe(() => this.getAllCoordinatorSubjects());
    }
    if (!this.substitutionListChangedSubscription) {
      this.substitutionListChangedSubscription = this.substitutionService.substitutionListChanged
        .pipe(debounceTime(400))
        .subscribe(() => this.getAllSubstitutionSubjects());
    }
    if (!this.lrbListChangedSubscription) {
      this.lrbListChangedSubscription = this.assessmentChemicalService.lrbListChanged
        .pipe(debounceTime(400))
        .subscribe(() => this.getAllAssessmentSubjects());
    }
    if (!this.arbListChangedSubscription) {
      this.arbListChangedSubscription = this.assessmentService.arbListChanged
        .pipe(debounceTime(400))
        .subscribe(() => this.getAllActivityAssessments());
    }
  }

  ngOnDestroy() {
    if (this.departmentChangedSubscription) {
      this.departmentChangedSubscription.unsubscribe();
    }
    this.productListChangedSubscription?.unsubscribe();
    this.catalogueListChangedSubscription?.unsubscribe();
    this.coordinatorListChangedSubscription?.unsubscribe();
    this.substitutionListChangedSubscription?.unsubscribe();
    this.lrbListChangedSubscription?.unsubscribe();
    this.arbListChangedSubscription?.unsubscribe();
    this.productListChangedSubscription = null;
    this.catalogueListChangedSubscription = null;
    this.coordinatorListChangedSubscription = null;
    this.substitutionListChangedSubscription = null;
    this.lrbListChangedSubscription = null;
    this.arbListChangedSubscription = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickedInside = this.host.nativeElement.contains(event.target as Node);
    if (!clickedInside) {
      this.isSettingsDropdownOpen.set(false);
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.isSettingsDropdownOpen.set(false);
    }
  }

  logout() {
    this.isSettingsDropdownOpen.set(false);
    this.authService.logout();
  }

  goToHelp() {
    this.isSettingsDropdownOpen.set(false);
    this.router.navigate(['/help']);
  }

  goToSettings() {
    this.isSettingsDropdownOpen.set(false);
    this.router.navigate(['/settings/profile']);
  }

  showContactForm() {
    this.isSettingsDropdownOpen.set(false);
    this.router.navigate(['/contactform']);
  }

  // Navigation methods
  navigateHome() {
    this.router.navigate(['/dashboard']);
  }

  goToCatalogue() {
    sessionStorage.setItem('fromNavigationLink', 'true');
    this.router.navigate(['/catalogue']);
  }

  goToProducts() {
    sessionStorage.setItem('fromNavigationLink', 'true');
    this.router.navigate(['/productlist']);
  }

  goToCoordinator() {
    sessionStorage.setItem('fromNavigationLink', 'true');
    this.router.navigate(['/coordinator']);
  }

  goToSubstitution() {
    sessionStorage.setItem('fromNavigationLink', 'true');
    this.router.navigate(['/substitution']);
  }

  gotoLocalRiskAssessments() {
    sessionStorage.setItem('fromNavigationLink', 'true');
    this.router.navigate(['/assessmentschemical']);
  }

  gotoActivityRiskAssessments() {
    sessionStorage.setItem('fromNavigationLink', 'true');
    this.router.navigate(['/assessments']);
  }

  goToAssessment() {
    sessionStorage.setItem('fromNavigationLink', 'true');
    this.router.navigate(['/assessments']);
  }

  goToAssessmentChemical() {
    sessionStorage.setItem('fromNavigationLink', 'true');
    this.router.navigate(['/assessmentschemical']);
  }

  goToExposures() {
    this.router.navigate(['/exposures']);
  }

  goToHistory() {
    this.router.navigate(['/history']);
  }

  gotoReports() {
    this.router.navigate(['/reports']);
  }

  gotoMaterialList() {
    this.router.navigate(['/material-list']);
  }

  gotoLaw() {
    this.router.navigate(['/law']);
  }

  gotoEducation() {
    this.router.navigate(['/education']);
  }

  gotoSamtec() {
    this.router.navigate(['/samtec']);
  }

  gotoControlPanel() {
    this.router.navigate(['/control-panel']);
  }

  toggleSettingsDropdown() {
    this.isSettingsDropdownOpen.set(!this.isSettingsDropdownOpen());
  }

  /** Recount the Produkter badge from the existing product list. */
  refreshProductCount(): void {
    const department = this.selectedDepartment();
    if (!department) {
      return;
    }
    this.loadProducts(department);
  }

  loadProducts(department) {
    if (this.selectedDepartment() == department) {
      this.getProducts().then(products => {
        if (products != null) {
          this.productsCount.set(0);
          this.productsLang = { en: 0, sv: 0, fi: 0 };
          this.products = products;
          this.products.forEach(product => {
            if (product?.override == null) {
              this.productsCount.set(this.productsCount() + 1);
            }

            if (product?.lang == 0) {
              this.productsLang["sv"]++;
            } else if (product?.lang == 1) {
              this.productsLang["en"]++;
            } else if (product?.lang == 2) {
              this.productsLang["fi"]++;
            }
          });
        }
      });
    }
  }

  loadCatalogue() {
    if (this.selectedDepartment()) {
      this.catalogueService.AllWithSuborganisations$(this.selectedDepartment().id).subscribe({
        next: catalogues => {
          if (Array.isArray(catalogues))
          {
            this.cataloguesCount.set(catalogues.length);
          }
        },
        error: error => {
          console.log("Unable to fetch catalogues: ", error);
        }
      })
    }
  }

  getAllCoordinatorSubjects() {
    if (this.selectedDepartment()) {
      this.coordinatorService.getAllSubs();
      this.coordinatorService.getAllCoordinatorDataListener().pipe(take(1)).subscribe({
        next: coordinatorSubjects => {
          if (coordinatorSubjects != null)
          {
            let count = 0;
            for (var i = 0; i < coordinatorSubjects.length; i += 1) {

              if (coordinatorSubjects[i].status === 0) {
                count += 1;
              }
              else if (coordinatorSubjects[i].status > 0 && coordinatorSubjects[i].status < 4) {
                count += 1;
              } else if (coordinatorSubjects[i].status === 4) {
                count += 1;
              }
            }
            this.coordinatorCount.set(count);
          }
        }
      })
    }
  }

  getAllSubstitutionSubjects() {
    const department = this.selectedDepartment();
    if (department) {
      this.substitutionService.getAll$().subscribe({
        next: substitutionSubjects => {
          if (Array.isArray(substitutionSubjects))
          {
            const departmentIds = this.utilService.getAllSubdepartments(department, true);
            const ids = Array.isArray(departmentIds) ? departmentIds : [department.id];
            const count = substitutionSubjects.filter(item =>
              ids.indexOf(item?.subdepartmentid) > -1
            ).length;
            this.substitutionCount.set(count);
          }
        }
      })
    }
  }

  getAllAssessmentSubjects() {
    this.assessmentChemicalService.getProductsWithSuborganisations();
    this.assessmentChemicalService.getAssessmentProductsDataListener().pipe(take(1)).subscribe({
      next: info => {
        if (info != null) {
          let count = 0;
          for (var n = 0; n < info.length; n++) {
            if ((info[n].chemical_id != null) && (info[n].created_date != null))
            {
              count += 1;
            }
          }
          this.lrbCount.set(count);
        }
      }
    })
  }

  getAllActivityAssessments() {
    this.assessmentService.AllWithSubDepartments();
    this.assessmentService.getAssessmentDataListener().pipe(take(1)).subscribe({
      next: info => {
        if (info != null) {
          this.arbCount.set(info.length);
        }
      }
    })
  }

  getProducts(): Promise<any[]> {
    if (this.selectedDepartment())
    {
      return new Promise<any>(resolve => {
        if (this.utilService.subscribesTo("subscription_stamped_SDS")) {

          this.productService.AllWithSuborganisationsMoreInformationStampedTop(this.selectedDepartment().id)
        } else {

          this.productService.AllWithSuborganisationsMoreInformationTop(this.selectedDepartment().id);
        }

        this.productService.getProductAllSubTopListener().pipe(take(1)).subscribe({
          next: products => {
            resolve(products);
          },
          error: error => {
            console.log("Error: ", error);
            this.utilService.onError(error);
          }
        });
      })
    }
  }
}

